"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* POS Section: optimized for beverage stores and wine/beer merchants */
export function BeveragePOS({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Soft blue-purple backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Left Column: Mockup Image inside curved Dome Shape backdrop bubble */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative flex justify-center py-6"
          >
            {/* Curved dome shape background bubble going to the left edge of the page */}
            <div className="absolute top-0 bottom-0 left-[-30%] right-[10%] bg-white/[0.03] rounded-r-[220px] -z-10 pointer-events-none" />

            <div className="rounded-[24px] overflow-hidden shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full max-w-[580px] relative">
              <Image
                src="/odoo/industries/beverage-store/beverage-pos.webp"
                alt="Beverage POS interface"
                width={580}
                height={400}
                className="w-full h-auto object-cover"
                onError={(e) => { e.target.src = "/odoo/unsplash/bar-pub-hero.jpg"; }}
              />
            </div>
          </motion.div>

          {/* Right Column: Title and Paragraphs */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 lg:offset-1 flex flex-col gap-6"
          >
            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-4`}>
              <span className="x_wd_blue_highlight_01 mr-2 inline-block">
                PoS
              </span>{" "}
              optimized for <br />
              beverages
            </h2>
            <div className="space-y-6">
              <p className="text-gray-300 leading-relaxed text-xl">
                <strong>All you need to run a beverage store:</strong> B2B pricelists, multiple packaging, fast product creation, consignments, etc.
              </p>
              <p className="text-gray-300 leading-relaxed text-xl">
                <strong>Run all the flows from your store:</strong> book online and pickup in store, buy in store and ask a delivery, return consignments, get an invoice on Whatsapp, etc.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* Online Shop Section: dual mockup B2B and B2C portal */
export function BeverageOnlineShop({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans text-center">
      {/* Soft yellow/green backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1250px] relative z-10">

        {/* Title Block */}
        <div className="flex flex-col items-center mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-6`}
          >
            <span className="x_wd_yellow_highlight_05 mr-2 inline-block">
              B2B
            </span>{" "}
            and{" "}
            <span className="x_wd_green_highlight_02 mx-2 inline-block">
              B2C
            </span>{" "}
            online shop
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-300 leading-relaxed max-w-3xl mx-auto text-xl"
          >
            <strong>Restaurants and cafés work when you don't.</strong> Let them order online with their account, and boost your business with online sales. They choose to pick up in store or order a delivery.
          </motion.p>
        </div>

        {/* Dual Mockup Composition using standard Odoo Bootstrap columns */}
        <div className="row align-items-center justify-content-center relative py-12">

          {/* Main Desktop Mockup (Framed White Card matching Bakery) - col-lg-8 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-lg-8 position-relative"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 relative">
              <Image
                src="/odoo/industries/beverage-store/pos-1.webp"
                alt="Beverage Shop Desktop View"
                width={800}
                height={550}
                sizes="(max-width: 1024px) 100vw, 70vw"
                className="w-full h-auto rounded-[18px] object-cover"
                onError={(e) => { e.target.src = "/odoo/industries/bakery/website-bakery-full.webp"; }}
              />
            </div>

            {/* Floating Tap Icon on Left side of main mockup - d-none d-lg-inline-block */}
            <div 
              className="position-absolute start-0 z-20 d-none d-lg-inline-block pointer-events-none select-none mt-7 w-[70px] h-[70px]"
              style={{ top: "35%", left: "-35px" }}
            >
              <motion.div
                animate={{ scale: [1, 1.15, 1], rotate: [200, 205, 200] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                className="w-full h-full relative"
              >
                <Image
                  src="/odoo/icons/yellow_tap.svg"
                  fill
                  className="object-contain"
                  alt=""
                  onError={(e) => { e.target.src = "/odoo/icons/tap.svg"; }}
                />
              </motion.div>
            </div>
          </motion.div>

          {/* Curved Vector Arrow - col-lg-1 d-none d-lg-block */}
          <div className="col-lg-1 d-none d-lg-block ms-n5 mt-5 z-3 position-relative w-[80px] h-[80px]">
            <Image
              src="/odoo/arrows/arrow_xl_02.svg"
              fill
              className="ms-0 object-contain"
              alt=""
            />
          </div>

          {/* Overlapping Cart Popup Drawer Mockup on bottom right - col-lg-4 d-none d-lg-block */}
          <motion.div
            initial={{ opacity: 0, x: 40, y: 40 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="col-lg-4 offset-lg-7 offset-lg-4 mt-lg-n10 position-relative d-none d-lg-block z-10"
            style={{ marginTop: "-110px" }}
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[20px] shadow-2xl relative">
              <Image
                src="/odoo/industries/beverage-store/pos-2.webp"
                alt="Beverage Cart Popup"
                width={400}
                height={450}
                className="w-full h-auto rounded-[14px] object-cover"
                onError={(e) => { e.target.src = "/odoo/industries/bakery/website-custom-order-bakery.webp"; }}
              />
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}

/* Inventory Section: modern inventory with barcode scanner graphics */
export function BeverageInventory({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Soft emerald backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row g-5 align-items-center justify-between">

          {/* Copywriting Column */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-lg-5 py-lg-6 text-start"
          >
            <div className="flex justify-start gap-4 mb-4 relative w-[200px] h-[50px]">
              <Image
                src="/odoo/graphics/barcode.svg"
                fill
                className="mb-3 select-none pointer-events-none object-contain"
                alt="Barcode"
                onError={(e) => { e.target.src = "https://odoocdn.com/openerp_website/static/src/img/graphics/barcode.svg"; }}
              />
            </div>

            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-6`}>
              Modern{" "}
              <span className="x_wd_red_highlight_bold_03 inline-block">
                inventory
              </span>{" "}
              <br />
              management
            </h2>

            <div className="space-y-6">
              <p className="text-gray-300 leading-relaxed text-xl">
                <strong>Receive, store and deliver faster with Odoo's barcode app.</strong> Buy pallets and sell by the bottle; we handle all the packagings for you.
              </p>
              <p className="text-gray-300 leading-relaxed text-xl">
                <strong>Bottles, or boxes, returns have never been so easy:</strong> never spill money due to missed recording. With Odoo purchase, replenishment has never been so easy.
              </p>
            </div>
          </motion.div>

          {/* Large Mockup Column */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="col-lg-6 py-5 position-relative flex justify-center"
          >
            {/* White dome-like background bubble */}
            <div className="absolute top-0 bottom-0 right-[-10%] left-[10%] bg-white/[0.03] rounded-l-[220px] -z-10 pointer-events-none" />

            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full max-w-[370px] relative z-10">
              <Image
                src="/odoo/industries/beverage-store/inventory.webp"
                alt="Inventory and Barcode Tracking"
                width={370}
                height={550}
                className="w-full h-auto rounded-[18px] object-cover"
                onError={(e) => { e.target.src = "https://odoocdn.com/openerp_website/static/src/img/industries/beverage-store/inventory.webp"; }}
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* Accounting Section: complete financial clarity in real-time */
export function BeverageAccounting({ section }) {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-transparent w-full font-sans">
      {/* Soft blue-purple backdrop glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row g-5 align-items-center justify-between">

          {/* Mockup Column */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="col-lg-6 order-2 order-lg-1 flex justify-center"
          >
            <div className="p-1.5 bg-white border border-gray-200 rounded-[24px] shadow-2xl hover:scale-[1.01] transition-transform duration-300 w-full max-w-[400px] relative z-10 x_wd_corner_highlight_03">
              <Image
                src="/odoo/industries/beverage-store/invoice.webp"
                alt="Beverage Accounting and Invoicing interface"
                width={400}
                height={550}
                className="w-full h-auto rounded-[18px] object-cover"
                onError={(e) => { e.target.src = "https://odoocdn.com/openerp_website/static/src/img/industries/beverage-store/invoice.webp"; }}
              />
            </div>
          </motion.div>

          {/* Copywriting Column */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="col-lg-5 offset-lg-1 order-1 order-lg-2 py-lg-6 text-start"
          >
            <div className="flex justify-start gap-4 mb-4 relative w-[70px] h-[70px]">
              <Image
                src="/odoo/icons/blue_document.svg"
                fill
                className="mb-3 rotate-10 select-none pointer-events-none object-contain"
                alt="Document"
                onError={(e) => { e.target.src = "https://odoocdn.com/openerp_website/static/src/img/icons/blue_document.svg"; }}
              />
            </div>

            <h2 className={`${caveat.className} text-5xl md:text-6xl lg:text-[70px] font-bold text-white leading-tight mb-6`}>
              Integrated Accounting
            </h2>

            <div className="space-y-6">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong>Imagine having all your data in real-time</strong> - from inventory to sales. Everything becomes easier: inventory valuation, customer statements, and more.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
